import React, { useEffect, useState } from 'react';
import { X, Loader2, Phone, MessageCircle } from 'lucide-react';
import { listingsApi } from '../api/client';
import { REGIONS, CATEGORIES, OFFERS } from '../data/mock';
import { ImagesUploader, VideoUploader } from './MediaUpload';

const LANGUAGES = ['Deutsch', 'Englisch', 'Französisch', 'Italienisch', 'Spanisch',
                   'Portugiesisch', 'Russisch', 'Polnisch', 'Rumänisch', 'Türkisch',
                   'Arabisch', 'Thai', 'Japanisch', 'Chinesisch'];

const EMPTY = {
  name: '', category: 'Sie sucht ihn', region: 'Zürich (Stadt)', city: '',
  age: '', description: '', phone: '', whatsapp: '',
  languages: [], service_tags: [], offers: 1, price: 'auf Anfrage',
  images: [], video_url: '', top: false,
};

const ListingForm = ({ open, onClose, onSuccess, initial = null, mode = 'create' }) => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    if (open) {
      setError(''); setFieldErrors({});
      if (initial) {
        setForm({
          name: initial.name || '',
          category: initial.category || 'Sie sucht ihn',
          region: initial.region || 'Zürich (Stadt)',
          city: initial.city || '',
          age: initial.age || '',
          description: initial.description || '',
          phone: initial.phone || '',
          whatsapp: initial.whatsapp || '',
          languages: initial.languages || [],
          service_tags: initial.service_tags || [],
          offers: initial.offers || 1,
          price: initial.price || 'auf Anfrage',
          images: initial.images && initial.images.length
            ? initial.images
            : (initial.image_url ? [initial.image_url] : []),
          video_url: initial.video_url || '',
          top: !!initial.top,
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, initial]);

  if (!open) return null;

  const regions = REGIONS.filter((r) => r !== 'Alle Regionen');
  const cats = CATEGORIES.filter((c) => c !== 'Alle');

  const toggleArr = (field, value) => {
    const arr = form[field].includes(value) ? form[field].filter((v) => v !== value) : [...form[field], value];
    setForm({ ...form, [field]: arr });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = 'Name min. 2 Zeichen';
    if (!form.description.trim() || form.description.trim().length < 20) errs.description = 'Beschreibung min. 20 Zeichen';
    if (form.age && (Number(form.age) < 18 || Number(form.age) > 99)) errs.age = 'Alter zwischen 18 und 99';
    const phoneRe = /^[0-9+\s()\-]{6,20}$/;
    if (form.phone && !phoneRe.test(form.phone)) errs.phone = 'Ungültige Telefonnummer';
    if (form.whatsapp && !phoneRe.test(form.whatsapp)) errs.whatsapp = 'Ungültige WhatsApp-Nummer';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setError(''); setLoading(true);
    try {
      const payload = {
        ...form,
        age: form.age ? Number(form.age) : null,
        offers: Math.max(Number(form.offers) || 1, form.service_tags.length || 0),
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        city: form.city.trim() || null,
        images: form.images || [],
        image_url: (form.images && form.images[0]) || null,
        video_url: form.video_url || null,
      };
      let result;
      if (mode === 'edit' && initial?.id) {
        result = await listingsApi.update(initial.id, payload);
      } else {
        result = await listingsApi.create(payload);
      }
      onSuccess && onSuccess(result);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = (name) =>
    `w-full bg-[#241a1f] border rounded-md px-3 py-2.5 text-sm text-white placeholder:text-[#5a4d48] focus:outline-none ${
      fieldErrors[name] ? 'border-[#a83a5b]' : 'border-[#2a1f24] focus:border-[#a83a5b]'
    }`;

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto">
      <div className="max-w-2xl w-full bg-[#1a1417] border border-[#2a1f24] rounded-2xl p-6 sm:p-7 relative my-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#7a6b65] hover:text-white" aria-label="Schliessen">
          <X className="w-5 h-5" />
        </button>
        <h2 className="font-serif text-2xl text-white mb-1">
          {mode === 'edit' ? 'Inserat bearbeiten' : 'Inserat aufgeben'}
        </h2>
        <p className="text-sm text-[#9a8a83] mb-5">Alle Felder mit * sind Pflichtfelder</p>

        <form onSubmit={submit} className="space-y-4">
          {/* Grundangaben */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5">Name / Titel *</label>
            <input type="text" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={fieldCls('name')} placeholder="z.B. Lisa 24, Studio Rose, ..." />
            {fieldErrors.name && <p className="text-xs text-[#ff8aa3] mt-1">{fieldErrors.name}</p>}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5">Kategorie *</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className={fieldCls('category')}>
                {cats.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5">Region *</label>
              <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })}
                className={fieldCls('region')}>
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5">Stadt / Ort</label>
              <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                className={fieldCls('city')} placeholder="z.B. Zürich-Oerlikon" />
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5">Alter</label>
              <input type="number" min={18} max={99} value={form.age}
                onChange={(e) => setForm({ ...form, age: e.target.value })}
                className={fieldCls('age')} placeholder="z.B. 24" />
              {fieldErrors.age && <p className="text-xs text-[#ff8aa3] mt-1">{fieldErrors.age}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5">Beschreibung *</label>
            <textarea required minLength={20} rows={4} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className={fieldCls('description')} placeholder="Beschreibe dich und dein Angebot ausführlich..." />
            <div className="flex justify-between mt-1">
              {fieldErrors.description ? (
                <p className="text-xs text-[#ff8aa3]">{fieldErrors.description}</p>
              ) : <span />}
              <p className="text-xs text-[#7a6b65]">{form.description.length} / 2000</p>
            </div>
          </div>

          {/* Kontakt */}
          <div className="pt-2 border-t border-[#2a1f24]">
            <h3 className="font-serif text-lg text-white mb-3">Kontakt</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5 inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Telefon
                </label>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className={fieldCls('phone')} placeholder="+41 79 123 45 67" />
                {fieldErrors.phone && <p className="text-xs text-[#ff8aa3] mt-1">{fieldErrors.phone}</p>}
              </div>
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5 inline-flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" /> WhatsApp
                </label>
                <input type="tel" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  className={fieldCls('whatsapp')} placeholder="+41 79 123 45 67" />
                {fieldErrors.whatsapp && <p className="text-xs text-[#ff8aa3] mt-1">{fieldErrors.whatsapp}</p>}
              </div>
            </div>
          </div>

          {/* Preise */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-1.5">Preis</label>
            <input type="text" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
              className={fieldCls('price')} placeholder="z.B. ab CHF 200 / 30min" />
          </div>

          {/* Medien */}
          <div className="pt-2 border-t border-[#2a1f24] space-y-4">
            <h3 className="font-serif text-lg text-white">Medien</h3>
            <ImagesUploader
              images={form.images}
              onChange={(imgs) => setForm({ ...form, images: imgs })}
              max={5}
            />
            <VideoUploader
              videoUrl={form.video_url}
              onChange={(url) => setForm({ ...form, video_url: url || '' })}
            />
          </div>

          {/* Services */}
          <div className="pt-2 border-t border-[#2a1f24]">
            <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-2">
              Angebote / Services ({form.service_tags.length} ausgewählt)
            </label>
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 bg-[#15101280] rounded-md border border-[#2a1f24]">
              {OFFERS.map((o) => {
                const on = form.service_tags.includes(o);
                return (
                  <button type="button" key={o} onClick={() => toggleArr('service_tags', o)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      on ? 'bg-[#a83a5b] border-[#a83a5b] text-white'
                         : 'bg-transparent border-[#2a1f24] text-[#b8a9a3] hover:border-[#a83a5b]'
                    }`}>
                    {o}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sprachen */}
          <div>
            <label className="block text-xs tracking-wider uppercase text-[#c19a8a] mb-2">Sprachen</label>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((l) => {
                const on = form.languages.includes(l);
                return (
                  <button type="button" key={l} onClick={() => toggleArr('languages', l)}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                      on ? 'bg-[#a83a5b] border-[#a83a5b] text-white'
                         : 'bg-transparent border-[#2a1f24] text-[#b8a9a3] hover:border-[#a83a5b]'
                    }`}>
                    {l}
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div className="p-3 rounded-md bg-[#3a1018] border border-[#7a2030] text-sm text-[#ffb3c1]">{error}</div>}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#2a1f24]">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-md border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b]">
              Abbrechen
            </button>
            <button type="submit" disabled={loading}
              className="px-5 py-2.5 rounded-md bg-[#a83a5b] hover:bg-[#bf456b] disabled:opacity-60 text-white font-medium inline-flex items-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'edit' ? 'Änderungen speichern' : 'Inserat veröffentlichen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ListingForm;
